import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../../../services/api'

/**
 * SPEC-031 Admin Console — content-mode detection (Constitution D3, dual-mode).
 *
 * When the hub runs in static-content mode (artist-pack JSON, no CMS database)
 * every admin mutation is rejected server-side with `501 CMS_ONLY`. This
 * provider gives the console a *proactive* signal so it can disable editing and
 * show a mode banner rather than letting the user try and fail. It mirrors the
 * API's own detection: the API sets `process.env.CONTENT_MODE`, and its public
 * `/health` document includes the `db` migration block **only** in CMS mode
 * (`apps/api/src/app.ts`), so `db` presence is the canonical web-side signal.
 *
 * Detection is best-effort: while the probe is in flight, and if it fails, we
 * default to CMS (`readOnly: false`) — the server still fail-closes every
 * mutation, so the client gate is a UX affordance, never a security boundary.
 */

export type ContentMode = 'cms' | 'static' | 'unknown'

interface ContentModeValue {
  /** Detected content mode (`unknown` while the health probe is in flight). */
  mode: ContentMode
  /** True only in static mode — surfaces should disable editing/mutations. */
  readOnly: boolean
  /** True while the initial health probe is resolving. */
  loading: boolean
}

const ContentModeContext = createContext<ContentModeValue>({ mode: 'unknown', readOnly: false, loading: true })

/**
 * Detect the hub's content mode and provide it to the console subtree.
 *
 * @param children - The console subtree.
 */
export function ContentModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setMode] = useState<ContentMode>('unknown')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    api
      .getHealth()
      .then((health) => {
        if (!active) return
        // The `db` block is present only when the API runs in CMS mode.
        setMode(health.db ? 'cms' : 'static')
      })
      .catch(() => {
        // Probe failed — assume CMS (editable). The server still gates mutations.
        if (active) setMode('unknown')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const value = useMemo<ContentModeValue>(() => ({ mode, readOnly: mode === 'static', loading }), [mode, loading])
  return <ContentModeContext.Provider value={value}>{children}</ContentModeContext.Provider>
}

/** Access the console's detected content mode (CMS vs static / read-only). */
export function useContentMode(): ContentModeValue {
  return useContext(ContentModeContext)
}
